import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, query, orderBy, onSnapshot, writeBatch } from 'firebase/firestore';
import { Lock, BarChart2, RefreshCw, Power, Trash2, LogOut, Save, Plus, X, Users, Target, History, LayoutGrid, Building2, ChevronRight, Calendar, User, Home as HomeIcon, Image } from 'lucide-react';

const createDefaultDivision = (id = null) => ({
  id: id || `div_${Date.now()}`,
  title: 'Divisi Baru',
  icon: 'LayoutGrid',
  color: 'indigo',
  desc: 'Deskripsi divisi...',
  members: []
});

const DEFAULT_ORG_DATA = {
  nama: "FKPO DIY",
  tagline: "Wadah Aspirasi Pelajar",
  description: "Forum Komunikasi Pengurus OSIS DIY hadir sebagai ruang kolaborasi untuk meningkatkan kualitas pelajar di DIY",
  visi: "Menjadikan FKPO DIY sebagai pionir dalam mewujudkan perubahan sosial yang progresif dan berkelanjutan melalui inovasi, kolaborasi, dan kepemimpinan yang inspiratif dan independen.",
  misi: [
    { title: "Meningkatkan Partisipasi", desc: "Mengajak seluruh anggota untuk aktif berpartisipasi dalam setiap kegiatan dan program organisasi." },
    { title: "Pemberdayaan SDM", desc: "Memberdayakan siswa supaya menjadi agen perubahan dengan memberikan pelatihan kepemimpinan." },
    { title: "Sustainabilitas", desc: "Menerapkan kaidah keberlanjutan dalam setiap aktivitas organisasi." },
    { title: "Transformasi Program", desc: "Melakukan transformasi dan keberlanjutan Program Kerja dengan inovasi baru." }
  ],
  sejarah: "FKPO DIY berdiri di bawah naungan Balai Pemuda dan Olahraga (BPO) Dinas Pendidikan Pemuda dan Olahraga DIY.",
  tentangLainnya: "FKPO (Forum Komunikasi Pengurus OSIS) DIY merupakan wadah aktif yang rutin memperbarui kepengurusannya setiap tahun di bawah naungan BPO Dinas Pendidikan Pemuda dan Olahraga DIY."
};

const DEFAULT_HOME_DATA = {
  heroImage: "/fkpo_baru.jpeg",
  backgroundImage: "/bg.jpg"
};

const TABS = [
  { id: 'home', label: 'Home', icon: HomeIcon },
  { id: 'voting', label: 'Pemilihan', icon: BarChart2 },
  { id: 'kandidat', label: 'Kandidat', icon: User },
  { id: 'kegiatan', label: 'Activities', icon: Calendar },
  { id: 'organisasi', label: 'Organisasi', icon: Building2 },
  { id: 'periode', label: 'Periode', icon: History },
  { id: 'struktur', label: 'Struktur', icon: Users },
];

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState('voting');
  
  const [votes, setVotes] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [isVotingOpen, setIsVotingOpen] = useState(true);
  
  const [candidates, setCandidates] = useState([
    { id: 1, docId: "candidate_1", name: "Kandidat 1", school: "ASAL SEKOLAH" },
    { id: 2, docId: "candidate_2", name: "Kandidat 2", school: "ASAL SEKOLAH" },
    { id: 3, docId: "candidate_3", name: "Kandidat 3", school: "ASAL SEKOLAH" }
  ]);
  
  const [orgData, setOrgData] = useState(DEFAULT_ORG_DATA);
  const [homeData, setHomeData] = useState(DEFAULT_HOME_DATA);
  const [orgDataLoading, setOrgDataLoading] = useState(false);
  
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodData, setPeriodData] = useState({ divisions: [], kegiatans: [] });
  const [newPeriod, setNewPeriod] = useState("");
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Period selector dropdown state
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Period Selector Component - displays a dropdown for selecting period
  const PeriodSelector = ({ onSelect }) => {
    return (
      <div className="relative">
        <button
          onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
        >
          <Calendar size={18} />
          {selectedPeriod ? selectedPeriod.tahun : 'Pilih Periode'}
          <ChevronRight className={`w-4 h-4 transition-transform ${showPeriodDropdown ? 'rotate-90' : ''}`} />
        </button>
        
        {showPeriodDropdown && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="p-2">
              <p className="text-slate-400 text-xs px-3 py-2 uppercase tracking-wider">Pilih Periode</p>
              {periods.map((period) => (
                <button
                  key={period.id}
                  onClick={() => {
                    selectPeriod(period);
                    setShowPeriodDropdown(false);
                    if (onSelect) onSelect(period);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                    selectedPeriod?.tahun === period.tahun
                      ? 'bg-indigo-600/20 text-indigo-400'
                      : 'text-white hover:bg-slate-800'
                  }`}
                >
                  <span className="font-medium">{period.tahun}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${period.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                    {period.isActive ? 'Aktif' : 'Nonaktif'}
                  </span>
                </button>
              ))}
              {periods.length === 0 && (
                <p className="text-slate-500 text-sm px-3 py-4 text-center">Belum ada periode</p>
              )}
            </div>
            <div className="border-t border-slate-800 p-2">
              <button
                onClick={() => {
                  setShowPeriodDropdown(false);
                  setActiveTab('periode');
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-400 hover:bg-indigo-600/10 rounded-lg transition-colors"
              >
                <Plus size={16} /> Tambah Periode Baru
              </button>
            </div>
          </div>
        )}
        
        {/* Backdrop to close dropdown when clicking outside */}
        {showPeriodDropdown && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowPeriodDropdown(false)}
          />
        )}
      </div>
    );
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === "fkpodiy123") {
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("Password salah!");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
  };

  const handleToggleVoting = async () => {
    try {
      await setDoc(doc(db, "config", "votingStatus"), { isOpen: !isVotingOpen });
    } catch (error) {
      console.error("Error toggling voting:", error);
      showMessage("Gagal mengubah status pemilihan", "error");
    }
  };

  const handleResetVotes = async () => {
    if (!window.confirm("PERINGATAN: Apakah Anda yakin ingin MENGHAPUS SEMUA SUARA?")) return;
    if (prompt("Ketik 'RESET' untuk konfirmasi:") !== "RESET") return;

    try {
      const batch = writeBatch(db);
      candidates.forEach(candidate => {
        const ref = doc(db, "votes", candidate.docId);
        batch.set(ref, { name: candidate.name, school: candidate.school, voteCount: 0 });
      });
      await batch.commit();
      showMessage("Semua suara telah di-reset");
    } catch (error) {
      console.error("Error resetting votes:", error);
      showMessage("Gagal mereset suara", "error");
    }
  };

  const saveCandidates = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      candidates.forEach(candidate => {
        const ref = doc(db, "votes", candidate.docId);
        batch.set(ref, { name: candidate.name, school: candidate.school, voteCount: candidate.voteCount || 0 }, { merge: true });
      });
      await batch.commit();
      showMessage("Kandidat berhasil disimpan!");
    } catch (error) {
      console.error("Error saving candidates:", error);
      showMessage("Gagal menyimpan kandidat", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateCandidate = (index, field, value) => {
    const newCandidates = [...candidates];
    newCandidates[index] = { ...newCandidates[index], [field]: value };
    setCandidates(newCandidates);
  };

  const addCandidate = () => {
    const newId = candidates.length + 1;
    setCandidates([...candidates, { id: newId, docId: `candidate_${newId}`, name: "", school: "", voteCount: 0 }]);
  };

  const removeCandidate = (index) => {
    setCandidates(candidates.filter((_, i) => i !== index));
  };

  const loadOrgData = async () => {
    try {
      const docRef = doc(db, 'organization', 'data');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setOrgData(docSnap.data());
      else setOrgData(DEFAULT_ORG_DATA);
    } catch (error) {
      console.error("Error loading org data:", error);
      setOrgData(DEFAULT_ORG_DATA);
    }
  };

  const loadHomeData = async () => {
    try {
      const docRef = doc(db, 'config', 'home');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setHomeData(docSnap.data());
      } else {
        setHomeData(DEFAULT_HOME_DATA);
      }
    } catch (error) {
      console.error("Error loading home data:", error);
      setHomeData(DEFAULT_HOME_DATA);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const saveHomeData = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'home'), { ...homeData, updatedAt: new Date().toISOString() }, { merge: true });
      showMessage("Data Home berhasil disimpan!");
    } catch (error) {
      console.error("Error saving home data:", error);
      showMessage("Gagal menyimpan data", "error");
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const updateHomeField = (field, value) => setHomeData(prev => ({ ...prev, [field]: value }));

  // eslint-disable-next-line no-unused-vars
  const saveOrgData = async () => {
    setOrgDataLoading(true);
    try {
      await setDoc(doc(db, 'organization', 'data'), { ...orgData, updatedAt: new Date().toISOString() }, { merge: true });
      showMessage("Data organisasi berhasil disimpan!");
    } catch (error) {
      console.error("Error saving org data:", error);
      showMessage("Gagal menyimpan data", "error");
    } finally {
      setOrgDataLoading(false);
    }
  };

  const updateOrgField = (field, value) => setOrgData(prev => ({ ...prev, [field]: value }));

  const updateMisi = (index, field, value) => {
    const newMisi = [...(orgData.misi || [])];
    newMisi[index] = { ...newMisi[index], [field]: value };
    setOrgData(prev => ({ ...prev, misi: newMisi }));
  };

  const addMisi = () => setOrgData(prev => ({ ...prev, misi: [...(prev.misi || []), { title: "Misi Baru", desc: "Deskripsi misi..." }] }));
  const removeMisi = (index) => setOrgData(prev => ({ ...prev, misi: (prev.misi || []).filter((_, i) => i !== index) }));

  const loadPeriods = async () => {
    try {
      const q = query(collection(db, 'periods'), orderBy('tahun', 'desc'));
      const querySnapshot = await getDocs(q);
      const periodList = [];
      querySnapshot.forEach((doc) => { periodList.push({ id: doc.id, ...doc.data() }); });
      
      if (periodList.length === 0) {
        const defaultDivisions = [
          createDefaultDivision('ketua'), createDefaultDivision('sekretaris'), createDefaultDivision('bendahara'),
          createDefaultDivision('program_kerja'), createDefaultDivision('sdm'), createDefaultDivision('humas'),
          createDefaultDivision('dana_usaha'), createDefaultDivision('multimedia')
        ];
        
        await setDoc(doc(db, 'periods', '2026-2027'), { tahun: '2026-2027', isActive: true, divisions: defaultDivisions, kegiatans: [], createdAt: new Date().toISOString() });
        await setDoc(doc(db, 'periods', '2025-2026'), { tahun: '2025-2026', isActive: false, divisions: defaultDivisions, kegiatans: [], createdAt: new Date().toISOString() });
        
        setPeriods([
          { id: '2026-2027', tahun: '2026-2027', isActive: true },
          { id: '2025-2026', tahun: '2025-2026', isActive: false }
        ]);
      } else {
        setPeriods(periodList);
      }
    } catch (error) {
      console.error("Error loading periods:", error);
    }
  };

  const addNewPeriod = async () => {
    if (!newPeriod.trim()) { showMessage("Masukkan nama periode", "error"); return; }
    if (periods.some(p => p.tahun === newPeriod)) { showMessage("Periode sudah ada!", "error"); return; }
    
    try {
      const defaultDivisions = [
        createDefaultDivision('ketua'), createDefaultDivision('sekretaris'), createDefaultDivision('bendahara'),
        createDefaultDivision('program_kerja'), createDefaultDivision('sdm'), createDefaultDivision('humas'),
        createDefaultDivision('dana_usaha'), createDefaultDivision('multimedia')
      ];
      
      await setDoc(doc(db, 'periods', newPeriod), { tahun: newPeriod, isActive: false, divisions: defaultDivisions, kegiatans: [], createdAt: new Date().toISOString() });
      setNewPeriod("");
      await loadPeriods();
      showMessage("Periode baru berhasil ditambahkan!");
    } catch (error) {
      console.error("Error adding period:", error);
      showMessage("Gagal menambahkan periode", "error");
    }
  };

  const deletePeriod = async (periodId) => {
    if (!window.confirm(`Hapus periode ${periodId}?`)) return;
    try {
      await deleteDoc(doc(db, 'periods', periodId));
      if (selectedPeriod?.tahun === periodId) { setSelectedPeriod(null); setPeriodData({ divisions: [], kegiatans: [] }); }
      await loadPeriods();
      showMessage("Periode dihapus");
    } catch (error) {
      console.error("Error deleting period:", error);
      showMessage("Gagal menghapus periode", "error");
    }
  };

  const selectPeriod = async (period) => {
    setSelectedPeriod(period);
    try {
      const docRef = doc(db, 'periods', period.tahun);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPeriodData({ divisions: data.divisions || [], kegiatans: data.kegiatans || [], backgroundImage: data.backgroundImage || '', heroImage: data.heroImage || '' });
      } else {
        setPeriodData({ divisions: [createDefaultDivision()], kegiatans: [], backgroundImage: '', heroImage: '' });
      }
    } catch (error) {
      console.error("Error loading period data:", error);
      setPeriodData({ divisions: [], kegiatans: [] });
    }
  };

  const savePeriodData = async () => {
    if (!selectedPeriod) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'periods', selectedPeriod.tahun), { ...periodData, updatedAt: new Date().toISOString() }, { merge: true });
      showMessage("Data periode berhasil disimpan!");
    } catch (error) {
      console.error("Error saving period data:", error);
      showMessage("Gagal menyimpan data", "error");
    } finally {
      setSaving(false);
    }
  };

  const updatePeriodField = (field, value) => {
    setPeriodData(prev => ({ ...prev, [field]: value }));
  };

  const updateDivision = (index, field, value) => {
    const divisions = periodData.divisions || [];
    const newDivisions = [...divisions];
    newDivisions[index] = { ...newDivisions[index], [field]: value };
    setPeriodData(prev => ({ ...prev, divisions: newDivisions }));
  };

  const addDivision = () => setPeriodData(prev => ({ ...prev, divisions: [...(prev.divisions || []), createDefaultDivision()] }));
  const removeDivision = (index) => setPeriodData(prev => ({ ...prev, divisions: (prev.divisions || []).filter((_, i) => i !== index) }));

  const addMember = (divisionIndex) => {
    const divisions = periodData.divisions || [];
    const newDivisions = [...divisions];
    newDivisions[divisionIndex].members = [...(newDivisions[divisionIndex].members || []), { name: "", school: "" }];
    setPeriodData(prev => ({ ...prev, divisions: newDivisions }));
  };

  const updateMember = (divisionIndex, memberIndex, field, value) => {
    const divisions = periodData.divisions || [];
    const newDivisions = [...divisions];
    newDivisions[divisionIndex].members = [...(newDivisions[divisionIndex].members || [])];
    newDivisions[divisionIndex].members[memberIndex] = { ...newDivisions[divisionIndex].members[memberIndex], [field]: value };
    setPeriodData(prev => ({ ...prev, divisions: newDivisions }));
  };

  const removeMember = (divisionIndex, memberIndex) => {
    const divisions = periodData.divisions || [];
    const newDivisions = [...divisions];
    newDivisions[divisionIndex].members = (newDivisions[divisionIndex].members || []).filter((_, i) => i !== memberIndex);
    setPeriodData(prev => ({ ...prev, divisions: newDivisions }));
  };

  const addActivity = () => {
    const kegiatans = periodData.kegiatans || [];
    setPeriodData(prev => ({ 
      ...prev, 
      kegiatans: [...kegiatans, { 
        title: "", 
        date: "", 
        location: "", 
        category: "Umum", 
        image: "", 
        description: "",
        type: "next"
      }] 
    }));
  };

  const updateActivity = (index, field, value) => {
    const kegiatans = periodData.kegiatans || [];
    const newKegiatans = [...kegiatans];
    newKegiatans[index] = { ...newKegiatans[index], [field]: value };
    setPeriodData(prev => ({ ...prev, kegiatans: newKegiatans }));
  };

  const removeActivity = (index) => {
    const kegiatans = periodData.kegiatans || [];
    setPeriodData(prev => ({ ...prev, kegiatans: kegiatans.filter((_, i) => i !== index) }));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadOrgData();
    loadHomeData();
    loadPeriods();

    const loadCandidates = async () => {
      try {
        const q = query(collection(db, "votes"));
        const querySnapshot = await getDocs(q);
        const dataMap = {};
        querySnapshot.forEach((doc) => { dataMap[doc.id] = doc.data(); });
        
        const defaultCandidates = [
          { id: 1, docId: "candidate_1", name: "Akram Fatahillah", school: "SMAIT ABU BAKAR" },
          { id: 2, docId: "candidate_2", name: "Ganik Gutami Romadhoni", school: "SMKN 1 PANJATAN" },
          { id: 3, docId: "candidate_3", name: "Raihan Akmal", school: "SMAN 1 WATES" }
        ];
        
        const updatedCandidates = defaultCandidates.map(c => ({
          ...c,
          ...dataMap[c.docId]
        }));
        setCandidates(updatedCandidates);
      } catch (error) {
        console.error("Error loading candidates:", error);
      }
    };
    
    loadCandidates();

    const q = query(collection(db, "votes"));
    const unsubscribeVotes = onSnapshot(q, (querySnapshot) => {
      const dataMap = {};
      querySnapshot.forEach((doc) => { dataMap[doc.id] = doc.data(); });
      
      setCandidates(prevCandidates => {
        const updatedCandidates = prevCandidates.map(c => ({
          ...c,
          voteCount: dataMap[c.docId]?.voteCount || 0
        }));
        
        updatedCandidates.sort((a, b) => b.voteCount - a.voteCount);
        
        const total = updatedCandidates.reduce((sum, c) => sum + c.voteCount, 0);
        setVotes(updatedCandidates);
        setTotalVotes(total);
        
        return updatedCandidates;
      });
    });

    const unsubscribeStatus = onSnapshot(doc(db, "config", "votingStatus"), (doc) => {
      if (doc.exists()) setIsVotingOpen(doc.data().isOpen);
      else setIsVotingOpen(true);
    });

    return () => { unsubscribeVotes(); unsubscribeStatus(); };
  }, [isAuthenticated]);

  const renderHomeTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2"><Image size={20} className="text-indigo-500" /> Pengaturan Tampilan Home per Periode</h3>
            <p className="text-slate-400 text-sm mt-1">Pilih periode untuk mengatur tampilan background dan hero image</p>
          </div>
          <PeriodSelector />
        </div>
        
        {!selectedPeriod ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-center">
            <p className="text-slate-400 mb-4">Silakan pilih periode terlebih dahulu</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Background Image untuk Periode {selectedPeriod.tahun}</label>
              <input 
                type="text" 
                value={periodData.backgroundImage || ''} 
                onChange={(e) => updatePeriodField('backgroundImage', e.target.value)} 
                placeholder="https://example.com/bg.jpg atau /bg.jpg"
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" 
              />
              <p className="text-slate-500 text-xs mt-1">Gambar background halaman Home</p>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Hero Image untuk Periode {selectedPeriod.tahun}</label>
              <input 
                type="text" 
                value={periodData.heroImage || ''} 
                onChange={(e) => updatePeriodField('heroImage', e.target.value)} 
                placeholder="https://example.com/hero.jpg atau /fkpo_baru.jpeg"
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" 
              />
              <p className="text-slate-500 text-xs mt-1">Gambar utama (kanan) di halaman Home</p>
            </div>
          </div>
        )}
      </div>

      {selectedPeriod && (
        <button onClick={savePeriodData} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-xl transition-colors disabled:opacity-50">
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Simpan Pengaturan
        </button>
      )}
    </div>
  );

  const renderVotingTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <RefreshCw size={20} className="text-indigo-500" /> Control Panel
        </h3>
        <div className="flex flex-wrap gap-4">
          <button onClick={handleToggleVoting} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${isVotingOpen ? 'bg-green-500/10 text-green-500 border border-green-500/20 hover:bg-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20'}`}>
            <Power size={18} /> {isVotingOpen ? 'Pemilihan Sedang DIBUKA' : 'Pemilihan DITUTUP'}
          </button>
          <button onClick={handleResetVotes} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors ml-auto">
            <Trash2 size={18} /> Reset Semua Suara
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm"><BarChart2 size={24} /></div>
            <div><p className="text-indigo-100 text-sm">Total Suara Masuk</p><h3 className="text-3xl font-bold">{totalVotes}</h3></div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {votes.map((candidate, index) => {
          const percentage = totalVotes > 0 ? ((candidate.voteCount / totalVotes) * 100).toFixed(1) : 0;
          const isWinner = index === 0 && totalVotes > 0;
          return (
            <div key={candidate.id} className={`relative overflow-hidden bg-slate-900 border rounded-2xl p-6 ${isWinner ? 'border-indigo-500 shadow-lg shadow-indigo-500/10' : 'border-slate-800'}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${isWinner ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400'}`}>{candidate.id}</div>
                  <div><h3 className="text-xl font-bold text-white">{candidate.name}</h3><p className="text-slate-400 text-sm">{candidate.school}</p></div>
                </div>
                <div className="text-right"><span className="text-3xl font-bold text-white block">{candidate.voteCount}</span><span className="text-indigo-400 text-sm font-medium">{percentage}%</span></div>
              </div>
              <div className="w-full bg-slate-800 h-4 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all duration-1000 ${isWinner ? 'bg-indigo-500' : 'bg-slate-600'}`} style={{ width: `${percentage}%` }}></div></div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderKandidatTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h3 className="text-white font-semibold">Kelola Kandidat Pemilihan</h3><p className="text-slate-400 text-sm">Edit nama dan sekolah kandidat</p></div>
        <button onClick={addCandidate} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"><Plus size={16} /> Tambah Kandidat</button>
      </div>

      <div className="space-y-4">
        {candidates.map((candidate, index) => (
          <div key={index} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">{candidate.id}</div>
              <div className="flex-1 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Nama Kandidat</label>
                  <input type="text" value={candidate.name} onChange={(e) => updateCandidate(index, 'name', e.target.value)} placeholder="Nama lengkap" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Asal Sekolah</label>
                  <input type="text" value={candidate.school} onChange={(e) => updateCandidate(index, 'school', e.target.value)} placeholder="Nama sekolah" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                </div>
              </div>
              <button onClick={() => removeCandidate(index)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={saveCandidates} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-xl transition-colors disabled:opacity-50">
        {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Simpan Kandidat
      </button>
    </div>
  );

  const renderkegiatantab = () => (
    <div className="space-y-6">
      {!selectedPeriod ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold">Edit Kegiatan</h3>
              <p className="text-slate-400 text-sm mt-1">Pilih periode untuk mengedit kegiatan</p>
            </div>
            <PeriodSelector />
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center">
            <p className="text-slate-400 mb-4">Silakan pilih periode terlebih dahulu</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div><h3 className="text-white font-semibold">Edit Kegiatan {selectedPeriod.tahun}</h3><p className="text-slate-400 text-sm">Tambah, edit, atau hapus kegiatan</p></div>
            <div className="flex gap-2">
              <button onClick={addActivity} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"><Plus size={16} /> Tambah Kegiatan</button>
              <button onClick={savePeriodData} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {(periodData.kegiatans || []).map((keg, index) => (
              <div key={index} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-indigo-400 font-semibold">#{index + 1}</span>
                  <button onClick={() => removeActivity(index)} className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={16} /></button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 text-xs mb-1">Judul Kegiatan</label>
                    <input type="text" value={keg.title} onChange={(e) => updateActivity(index, 'title', e.target.value)} placeholder="Nama kegiatan" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Tanggal</label>
                    <input type="text" value={keg.date} onChange={(e) => updateActivity(index, 'date', e.target.value)} placeholder="Contoh: Januari 2026" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Lokasi</label>
                    <input type="text" value={keg.location} onChange={(e) => updateActivity(index, 'location', e.target.value)} placeholder="Tempat kegiatan" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Kategori</label>
                    <input type="text" value={keg.category} onChange={(e) => updateActivity(index, 'category', e.target.value)} placeholder="Contoh: Sosial, Pendidikan" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Tipe Kegiatan</label>
                    <select 
                      value={keg.type || "next"} 
                      onChange={(e) => updateActivity(index, 'type', e.target.value)}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500"
                    >
                      <option value="next">Next Event (Akan Datang)</option>
                      <option value="history">History (Sudah Terlaksana)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">URL Gambar</label>
                    <input type="text" value={keg.image} onChange={(e) => updateActivity(index, 'image', e.target.value)} placeholder="https://example.com/gambar.jpg" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 text-xs mb-1">Deskripsi</label>
                    <textarea value={keg.description} onChange={(e) => updateActivity(index, 'description', e.target.value)} placeholder="Deskripsi kegiatan..." rows={3} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  </div>
                </div>
              </div>
            ))}
            {(!periodData.kegiatans || periodData.kegiatans.length === 0) && (
              <div className="text-center py-12 bg-slate-900 border border-slate-800 rounded-xl"><p className="text-slate-500">Belum ada kegiatan. Klik "Tambah Kegiatan" untuk menambahkan.</p></div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderOrganisasiTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-6 flex items-center gap-2"><Building2 size={20} className="text-indigo-500" /> Data Organisasi</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-slate-400 text-sm mb-2">Nama Organisasi</label>
            <input type="text" value={orgData.nama} onChange={(e) => updateOrgField('nama', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-slate-400 text-sm mb-2">Tagline</label>
            <input type="text" value={orgData.tagline} onChange={(e) => updateOrgField('tagline', e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-slate-400 text-sm mb-2">Deskripsi</label>
            <textarea value={orgData.description} onChange={(e) => updateOrgField('description', e.target.value)} rows={3} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-6 flex items-center gap-2"><History size={20} className="text-indigo-500" /> Sejarah</h3>
        <textarea value={orgData.sejarah} onChange={(e) => updateOrgField('sejarah', e.target.value)} rows={4} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-6 flex items-center gap-2"><Target size={20} className="text-indigo-500" /> Visi</h3>
        <textarea value={orgData.visi} onChange={(e) => updateOrgField('visi', e.target.value)} rows={4} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-semibold flex items-center gap-2"><LayoutGrid size={20} className="text-indigo-500" /> Misi</h3>
          <button onClick={addMisi} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"><Plus size={16} /> Tambah Misi</button>
        </div>
        <div className="space-y-4">
          {(orgData.misi || []).map((misi, index) => (
            <div key={index} className="bg-slate-950 border border-slate-800 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-3">
                  <input type="text" value={misi.title} onChange={(e) => updateMisi(index, 'title', e.target.value)} placeholder="Judul Misi" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                  <textarea value={misi.desc} onChange={(e) => updateMisi(index, 'desc', e.target.value)} placeholder="Deskripsi Misi" rows={2} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                </div>
                <button onClick={() => removeMisi(index)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><X size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={saveOrgData} disabled={orgDataLoading} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-xl transition-colors disabled:opacity-50">
        {orgDataLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Simpan Data Organisasi
      </button>
    </div>
  );

  const renderPeriodeTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Plus size={20} className="text-indigo-500" /> Tambah Periode Baru</h3>
        <div className="flex gap-4">
          <input type="text" value={newPeriod} onChange={(e) => setNewPeriod(e.target.value)} placeholder="Contoh: 2027-2028" className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500" />
          <button onClick={addNewPeriod} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"><Plus size={20} /> Tambah</button>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-4">Daftar Periode</h3>
        <div className="space-y-3">
          {periods.map((period) => (
            <div key={period.id} className={`flex items-center justify-between p-4 rounded-lg border ${selectedPeriod?.tahun === period.tahun ? 'bg-indigo-600/10 border-indigo-500/50' : 'bg-slate-950 border-slate-800'}`}>
              <div className="flex items-center gap-4">
                <button onClick={() => { selectPeriod(period); setActiveTab('kegiatan'); }} className="flex items-center gap-3">
                  <ChevronRight className={`w-5 h-5 text-slate-400 ${selectedPeriod?.tahun === period.tahun ? 'text-indigo-400' : ''}`} />
                  <span className="text-white font-medium">{period.tahun}</span>
                  <span className={`text-xs px-2 py-1 rounded ${period.isActive ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>{period.isActive ? 'Aktif' : 'Nonaktif'}</span>
                </button>
              </div>
              <button onClick={() => deletePeriod(period.tahun)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStrukturTab = () => (
    <div className="space-y-6">
      {!selectedPeriod ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold">Struktur Kepengurusan</h3>
              <p className="text-slate-400 text-sm mt-1">Pilih periode untuk mengedit struktur</p>
            </div>
            <PeriodSelector />
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center">
            <p className="text-slate-400 mb-4">Silakan pilih periode terlebih dahulu</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div><h3 className="text-white font-semibold">Struktur Kepengurusan {selectedPeriod.tahun}</h3><p className="text-slate-400 text-sm">Klik nama divisi untuk edit, tambah/hapus anggota</p></div>
            <div className="flex gap-2">
              <button onClick={addDivision} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm"><Plus size={16} /> Tambah Divisi</button>
              <button onClick={savePeriodData} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50">{saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Simpan</button>
            </div>
          </div>

          <div className="space-y-4">
            {(periodData.divisions || []).map((division, divIndex) => (
              <div key={divIndex} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 bg-slate-800/50 border-b border-slate-800">
                  <div className="flex items-center justify-between">
                    <input type="text" value={division.title} onChange={(e) => updateDivision(divIndex, 'title', e.target.value)} className="bg-transparent text-indigo-400 font-semibold focus:outline-none text-lg" />
                    <div className="flex items-center gap-2">
                      <button onClick={() => addMember(divIndex)} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"><Plus size={14} /> Anggota</button>
                      <button onClick={() => removeDivision(divIndex)} className="p-1.5 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <textarea value={division.desc} onChange={(e) => updateDivision(divIndex, 'desc', e.target.value)} rows={2} className="w-full mt-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-300 text-sm focus:outline-none focus:border-indigo-500" />
                </div>
                <div className="p-4 space-y-3">
                  {(division.members || []).map((member, memIndex) => (
                    <div key={memIndex} className="flex items-center gap-3">
                      <input type="text" value={member.name} onChange={(e) => updateMember(divIndex, memIndex, 'name', e.target.value)} placeholder="Nama Anggota" className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                      <input type="text" value={member.school} onChange={(e) => updateMember(divIndex, memIndex, 'school', e.target.value)} placeholder="Asal Sekolah" className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
                      <button onClick={() => removeMember(divIndex, memIndex)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  {(division.members || []).length === 0 && (
                    <p className="text-slate-500 text-sm italic py-2">Belum ada anggota. Klik "Anggota" untuk menambah.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'home': return renderHomeTab();
      case 'voting': return renderVotingTab();
      case 'kandidat': return renderKandidatTab();
      case 'kegiatan': return renderkegiatantab();
      case 'organisasi': return renderOrganisasiTab();
      case 'periode': return renderPeriodeTab();
      case 'struktur': return renderStrukturTab();
      default: return renderVotingTab();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <form onSubmit={handleLogin} className="bg-slate-900 border border-slate-800 p-8 rounded-2xl w-full max-w-md shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-indigo-600/20 rounded-full text-indigo-500"><Lock size={32} /></div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Admin Panel FKPO</h2>
          <input 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white mb-4 focus:border-indigo-500 outline-none transition-all"
            placeholder="Password"
          />
          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl transition-all">Masuk</button>
          {error && <p className="text-red-500 text-sm mt-4 text-center">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-slate-900 border-r border-slate-800 p-4">
        <div className="flex items-center gap-3 mb-8 px-2">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">F</div>
          <span className="text-white font-bold tracking-wider">ADMIN PANEL</span>
        </div>
        <nav className="space-y-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
            >
              <tab.icon size={18} /> {tab.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="mt-8 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10">
          <LogOut size={18} /> Keluar
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        <header className="mb-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white capitalize">{activeTab}</h1>
          {message && <div className={`px-4 py-2 rounded-lg text-sm text-white ${message.type === 'error' ? 'bg-red-600' : 'bg-green-600 animate-pulse'}`}>{message.text}</div>}
        </header>
        <div className="max-w-5xl">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default Admin;